import { RequestContextClient } from './request-context-client';

/**
 * @callback Get
 * @param {string} key The key of the value to retrieve.
 * @returns {any | undefined} The value associated with the key, or `undefined` if the key does not exist.
 */
/**
 * @callback Set
 * @param {string | object} key - The key to set, or an object of key-value pairs.
 * @param {*} value The value to associate with the key.
 */
/**
 * @callback GetClientData
 * @returns {Object | undefined} returns all data marked as `sendToClient`.
 */
/**
 * @typedef RequestContext
 * @property {Get} get Retrieves a value from the context based on the provided key.
 * @property {Set} set Sets a key-value pair in the context.
 * @property {GetClientData} getClientData returns all data marked as `sendToClient`.
 */

/**
 *
 * Initializes the request context store based on the environment.
 * In a Node.js environment, it's using `AsyncLocalStorage` instance from `server.js`.
 * In a client-side environment, we are initializing context.
 * @returns {AsyncLocalStorage | RequestContextClient} The initialized context store.
 */
const context = global.AsyncLocalStorage ?? new RequestContextClient();

function isObject(o) {
  return typeof o === 'object' && o !== null && !Array.isArray(o);
}

function getStore() {
  return context.getStore?.();
}

/**
 * The singleton instance of the request context manager.
 * Provides methods to `get`, `set`, and `run` (in Node.js) values within the request context.
 * @type {RequestContext}
 */
export const requestContext = {
  /**
   * @type Get
   */
  get: key => {
    const store = getStore();

    return store?.[key];
  },

  /**
   *
   * @param {object|string|number} key
   * @param value
   */
  set: (key, value) => {
    const store = getStore();

    if (!store) return;

    if (isObject(key)) {
      // e.g. set({ prop: 'val' }) => add the obj props to store root
      Object.keys(key).forEach(prop => {
        store[prop] = key[prop];
      });
    } else if (isObject(store[key])) {
      // e.g. set('loggerMetadata', { prop: 'val' }) and store.loggerMetadata already exists
      store[key] = { ...store[key], ...value };
    } else {
      // e.g. set('loggerMetadata', { prop: 'val' }) and store.loggerMetadata does not exist
      store[key] = value;
    }
  },

  /**
   * wrapper for https://nodejs.org/api/async_context.html#asynclocalstoragerunstore-callback-args
   */
  run: (store, callback) => {
    return context.run(store, callback);
  },

  /**
   * @type GetClientData
   */
  getClientData: () => {
    const store = getStore();

    if (!store) return {};

    return Object.entries(store).reduce((clientData, [key, value]) => {
      if (value?.sendToClient) {
        // eslint-disable-next-line no-param-reassign
        clientData[key] = value;
      }
      return clientData;
    }, {});
  },

  getStore,
};

global.EDM = global.EDM || {};
global.EDM.Venom = global.EDM.Venom || {};
global.EDM.Venom.debug = global.EDM.Venom.debug || {};

global.EDM.Venom.debug.requestContext = requestContext;
